import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Select } from '../../components/atoms/Select'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import type {
  CreateSolutionInput,
  SolutionDto,
  SolutionVariantPickerEntry,
  UpdateSolutionInput
} from '../../../viewmodels/useSolutions'

const NAME_MAX = 128
const DESC_MAX = 2048
const ALT_TEXT_MAX = 256
const IMAGE_MAX_BYTES = 5 * 1024 * 1024
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
const ACCEPT_TYPES = [...IMAGE_TYPES].join(',')

export type SolutionFormSubmit =
  | { kind: 'create'; payload: CreateSolutionInput }
  | { kind: 'update'; payload: UpdateSolutionInput }

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: SolutionDto | null
  readonly variantPicker: ReadonlyArray<SolutionVariantPickerEntry>
  readonly onClose: () => void
  readonly onSubmit: (
    input: SolutionFormSubmit
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}

interface VariantLine {
  variant_id: string
  quantity: number
  sort_order: number
}

export function SolutionFormModal({
  open,
  mode,
  initial,
  variantPicker,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [name, setName] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [altText, setAltText] = useState<string>('')
  const [lines, setLines] = useState<VariantLine[]>([])
  const [pickerSelection, setPickerSelection] = useState<string>('')
  const [newFile, setNewFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    if (initial === null) {
      setName('')
      setDescription('')
      setAltText('')
      setLines([])
      setNewFile(null)
    } else {
      setName(initial.name)
      setDescription(initial.description)
      const firstAlt = initial.media[0]?.alt_text ?? null
      setAltText(firstAlt ?? '')
      setLines(
        initial.variants.map((v) => ({
          variant_id: v.variant_id,
          quantity: v.quantity,
          sort_order: v.sort_order
        }))
      )
      setNewFile(null)
    }
    setPickerSelection('')
    setFieldError(null)
    setFormError(null)
    setSubmitting(false)
  }, [open, initial?.id])

  // Picker excludes variants already linked.
  const availablePicker = useMemo<SolutionVariantPickerEntry[]>(() => {
    const linked = new Set(lines.map((l) => l.variant_id))
    return variantPicker.filter((p) => !linked.has(p.variant_id))
  }, [lines, variantPicker])

  const derivedPricePaisa = useMemo<number | null>(() => {
    let total = 0
    let anyMissing = false
    const byId = new Map(variantPicker.map((p) => [p.variant_id, p]))
    // include initial-variant prices too — picker covers them all, but
    // defensive in case picker hasn't loaded yet.
    if (initial !== null) {
      for (const v of initial.variants) {
        if (!byId.has(v.variant_id)) {
          byId.set(v.variant_id, {
            variant_id: v.variant_id,
            product_id: v.product_id,
            product_name: v.product_name,
            axes_label: v.axes_label,
            selling_price_paisa: v.unit_selling_price_paisa
          })
        }
      }
    }
    for (const line of lines) {
      const p = byId.get(line.variant_id)
      if (p === undefined || p.selling_price_paisa === null) {
        anyMissing = true
        continue
      }
      total += p.selling_price_paisa * line.quantity
    }
    return anyMissing || lines.length === 0 ? null : total
  }, [lines, variantPicker, initial])

  const addLineFromPicker = (): void => {
    if (pickerSelection === '') return
    setLines((s) => [
      ...s,
      { variant_id: pickerSelection, quantity: 1, sort_order: s.length }
    ])
    setPickerSelection('')
  }

  const updateLineQty = (variantId: string, qty: number): void => {
    setLines((s) =>
      s.map((l) => (l.variant_id === variantId ? { ...l, quantity: qty } : l))
    )
  }

  const removeLine = (variantId: string): void => {
    setLines((s) => s.filter((l) => l.variant_id !== variantId))
  }

  const handleFile = (files: FileList | null): void => {
    if (files === null || files.length === 0) return
    const f = files[0]
    if (f === undefined) return
    if (!IMAGE_TYPES.has(f.type)) {
      setFormError(`Image must be JPEG / PNG / WebP (got ${f.type})`)
      return
    }
    if (f.size > IMAGE_MAX_BYTES) {
      setFormError(`Image ${formatBytes(f.size)} exceeds ${formatBytes(IMAGE_MAX_BYTES)} cap`)
      return
    }
    setFormError(null)
    setNewFile(f)
  }

  const handleSubmit = async (): Promise<void> => {
    setFieldError(null)
    setFormError(null)
    const trimmedName = name.trim()
    if (trimmedName.length < 1 || trimmedName.length > NAME_MAX) {
      setFieldError({ field: 'name', message: `name must be 1–${NAME_MAX} chars` })
      return
    }
    const trimmedDesc = description.trim()
    if (trimmedDesc.length < 1 || trimmedDesc.length > DESC_MAX) {
      setFieldError({ field: 'description', message: `description must be 1–${DESC_MAX} chars` })
      return
    }
    if (lines.length === 0) {
      setFieldError({ field: 'variants', message: 'A solution must have at least one variant' })
      return
    }
    for (const l of lines) {
      if (!Number.isInteger(l.quantity) || l.quantity < 1) {
        setFieldError({ field: 'variants', message: 'Every variant qty must be a positive integer' })
        return
      }
    }
    const trimmedAlt = altText.trim()
    if (trimmedAlt.length > ALT_TEXT_MAX) {
      setFieldError({ field: 'altText', message: `altText exceeds ${ALT_TEXT_MAX} chars` })
      return
    }
    if (mode === 'create' && newFile === null) {
      setFieldError({ field: 'image', message: 'A hero image is required' })
      return
    }

    setSubmitting(true)
    let res: { ok: true } | { ok: false; field: string | null; message: string }
    if (mode === 'create') {
      const payload: CreateSolutionInput = {
        name: trimmedName,
        description: trimmedDesc,
        variants: lines.map((l, i) => ({ ...l, sort_order: i })),
        altText: trimmedAlt === '' ? null : trimmedAlt,
        imageFile: newFile as File
      }
      res = await onSubmit({ kind: 'create', payload })
    } else {
      const payload: UpdateSolutionInput = {}
      if (initial === null || trimmedName !== initial.name) payload.name = trimmedName
      if (initial === null || trimmedDesc !== initial.description) payload.description = trimmedDesc
      // Variants always patched (sort_order normalised to position).
      payload.variants = lines.map((l, i) => ({ ...l, sort_order: i }))
      const initialAlt = initial?.media[0]?.alt_text ?? null
      const newAlt = trimmedAlt === '' ? null : trimmedAlt
      if (newAlt !== initialAlt) payload.altText = newAlt
      if (newFile !== null) payload.imageFile = newFile
      res = await onSubmit({ kind: 'update', payload })
    }
    setSubmitting(false)
    if (res.ok) {
      onClose()
      return
    }
    if (res.field !== null) {
      setFieldError({ field: res.field, message: res.message })
    } else {
      setFormError(res.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add solution' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field
          label="Name"
          error={fieldError?.field === 'name' ? fieldError.message : null}
        >
          <Input
            value={name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Nursery Starter Bundle"
            maxLength={NAME_MAX}
          />
        </Field>

        <Field
          label="Description"
          error={fieldError?.field === 'description' ? fieldError.message : null}
        >
          <Textarea
            value={description}
            rows={3}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What the solution is and what it includes"
            maxLength={DESC_MAX}
          />
        </Field>

        <fieldset className="space-y-3 rounded-lg border border-border bg-bg-primary/40 p-4">
          <legend className="px-1 font-body text-body-sm font-semibold text-charcoal">
            Hero image
            {mode === 'create' ? <span className="text-rust"> *</span> : null}
          </legend>
          {initial?.thumbnail_url !== null && initial?.thumbnail_url !== undefined && newFile === null ? (
            <div className="flex items-center gap-3">
              <img
                src={initial.thumbnail_url}
                alt={initial.name}
                className="w-20 h-20 rounded object-cover bg-parchment/40"
                crossOrigin="use-credentials"
              />
              <span className="text-body-sm text-text-secondary">
                Current image — pick a new file to replace.
              </span>
            </div>
          ) : null}
          {newFile !== null ? (
            <div className="text-body-sm text-charcoal">
              New: <span className="font-mono">{newFile.name}</span>{' '}
              <span className="text-text-secondary">({formatBytes(newFile.size)})</span>
            </div>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_TYPES}
            onChange={(e) => handleFile(e.target.files)}
            className="block w-full text-body-sm text-charcoal file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-honey/30 file:text-charcoal file:cursor-pointer file:hover:bg-honey/40"
          />
          {fieldError?.field === 'image' ? (
            <p className="text-body-xs text-rust">{fieldError.message}</p>
          ) : null}
          <Field
            label="Alt text"
            helper="Short description for accessibility."
            error={fieldError?.field === 'altText' ? fieldError.message : null}
          >
            <Input
              value={altText}
              hasError={fieldError?.field === 'altText'}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Optional"
              maxLength={ALT_TEXT_MAX}
            />
          </Field>
        </fieldset>

        <fieldset className="space-y-3 rounded-lg border border-border bg-bg-primary/40 p-4">
          <legend className="px-1 font-body text-body-sm font-semibold text-charcoal">
            Variants
            <span className="text-rust"> *</span>
          </legend>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select
                value={pickerSelection}
                onChange={(e) => setPickerSelection(e.target.value)}
              >
                <option value="">— Add a variant —</option>
                {availablePicker.map((p) => (
                  <option key={p.variant_id} value={p.variant_id}>
                    {p.product_name}
                    {p.axes_label !== null ? ` · ${p.axes_label}` : ''}
                    {p.selling_price_paisa !== null
                      ? ` · ${formatRupees(p.selling_price_paisa)}`
                      : ' · (unpriced)'}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              variant="secondary"
              onClick={addLineFromPicker}
              disabled={pickerSelection === ''}
            >
              Add
            </Button>
          </div>
          {fieldError?.field === 'variants' ? (
            <p className="text-body-xs text-rust">{fieldError.message}</p>
          ) : null}
          {lines.length === 0 ? (
            <p className="text-body-sm text-text-secondary text-center py-4">
              No variants added yet. Pick from the dropdown above.
            </p>
          ) : (
            <TableShell>
              <table className="w-full font-body text-body-sm">
                <thead className="bg-parchment/50 text-text-secondary">
                  <tr>
                    <Th>Variant</Th>
                    <Th className="text-right w-24">Qty</Th>
                    <Th className="text-right w-32">Unit price</Th>
                    <Th className="text-right w-32">Line total</Th>
                    <Th className="w-20"> </Th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const p =
                      variantPicker.find((v) => v.variant_id === l.variant_id) ??
                      initial?.variants.find((v) => v.variant_id === l.variant_id)
                    const productName =
                      p === undefined
                        ? l.variant_id
                        : 'product_name' in p
                          ? p.product_name
                          : l.variant_id
                    const axesLabel = p !== undefined && 'axes_label' in p ? p.axes_label : null
                    const unitPrice =
                      p !== undefined
                        ? 'selling_price_paisa' in p
                          ? p.selling_price_paisa
                          : (p as { unit_selling_price_paisa: number | null }).unit_selling_price_paisa
                        : null
                    const lineTotal = unitPrice !== null ? unitPrice * l.quantity : null
                    return (
                      <tr key={l.variant_id} className="border-t border-border">
                        <Td>
                          <div className="text-charcoal">{productName}</div>
                          {axesLabel !== null ? (
                            <div className="text-text-secondary text-body-xs">{axesLabel}</div>
                          ) : null}
                        </Td>
                        <Td className="text-right">
                          <Input
                            type="number"
                            inputMode="numeric"
                            min={1}
                            step={1}
                            value={String(l.quantity)}
                            onChange={(e) => {
                              const raw = e.target.value.trim()
                              if (raw === '') return
                              const n = Number(raw)
                              if (Number.isInteger(n) && n >= 1) updateLineQty(l.variant_id, n)
                            }}
                            className="text-right w-20"
                          />
                        </Td>
                        <Td className="text-right font-mono">
                          {unitPrice !== null ? formatRupees(unitPrice) : <span className="text-text-secondary">—</span>}
                        </Td>
                        <Td className="text-right font-mono">
                          {lineTotal !== null ? formatRupees(lineTotal) : <span className="text-text-secondary">—</span>}
                        </Td>
                        <Td>
                          <button
                            type="button"
                            className="text-body-xs underline underline-offset-2 text-rust hover:text-charcoal"
                            onClick={() => removeLine(l.variant_id)}
                          >
                            Remove
                          </button>
                        </Td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </TableShell>
          )}
          <div className="flex items-center justify-end gap-3 pt-2">
            <span className="text-body-sm text-text-secondary">Solution price</span>
            <span className="font-mono text-body font-semibold text-charcoal">
              {derivedPricePaisa !== null ? formatRupees(derivedPricePaisa) : '—'}
            </span>
          </div>
        </fieldset>
      </div>
    </Modal>
  )
}

function formatRupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
